import { useEffect } from 'react';
import { Extension, type Editor } from '@tiptap/core';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import type { Node as ProseMirrorNode } from '@tiptap/pm/model';
import { Decoration, DecorationSet } from '@tiptap/pm/view';
import { getLeafNodePlainText } from './document-plain-text';
import {
  subscribeDocumentSpeech,
  type DocumentSpeechSourceRange,
  type DocumentSpeechSpokenPlainRange,
} from './document-speech';
export const DOCUMENT_SPEECH_HIGHLIGHT_CLASS = 'document-speech-highlight';

export type DocumentSpeechHighlightRange = {
  from: number;
  to: number;
};

export const documentSpeechHighlightPluginKey = new PluginKey<DecorationSet>(
  'documentSpeechHighlight',
);

const rangesEqual = (
  left: DocumentSpeechHighlightRange | null,
  right: DocumentSpeechHighlightRange | null,
): boolean => {
  if (left === right) {
    return true;
  }
  if (!left || !right) {
    return false;
  }
  return left.from === right.from && left.to === right.to;
};

export const mapPlainTextOffsetsToDocRange = (
  doc: ProseMirrorNode,
  sectionFrom: number,
  sectionTo: number,
  plainFrom: number,
  plainTo: number,
  blockSeparator: string = '\n',
): DocumentSpeechHighlightRange | null => {
  if (plainFrom >= plainTo) {
    return null;
  }

  const positions: number[] = [];
  let plainOffset = 0;
  let firstBlock = true;

  doc.nodesBetween(sectionFrom, sectionTo, (node, pos) => {
    if (node.isBlock) {
      if (!firstBlock && blockSeparator) {
        for (let i = 0; i < blockSeparator.length; i += 1) {
          positions[plainOffset] = Math.min(Math.max(pos, sectionFrom), sectionTo);
          plainOffset += 1;
        }
      }
      firstBlock = false;
    }

    if (node.isText && node.text) {
      const textStart = Math.max(pos, sectionFrom);
      const textEnd = Math.min(pos + node.nodeSize, sectionTo);
      const localStart = textStart - pos;
      const localEnd = textEnd - pos;
      const slice = node.text.slice(localStart, localEnd);
      for (let i = 0; i < slice.length; i += 1) {
        positions[plainOffset] = textStart + i;
        plainOffset += 1;
      }
      return false;
    }

    if (node.isLeaf && !node.isText) {
      const leaf = getLeafNodePlainText(node);
      for (let i = 0; i < leaf.length; i += 1) {
        positions[plainOffset] = Math.min(pos, sectionTo);
        plainOffset += 1;
      }
      return false;
    }

    return true;
  });

  positions[plainOffset] = Math.min(sectionTo, doc.content.size);

  const clampedFrom = Math.max(0, Math.min(plainFrom, positions.length - 1));
  const clampedTo = Math.max(clampedFrom, Math.min(plainTo, positions.length - 1));
  const from = positions[clampedFrom];
  const to = positions[clampedTo];
  if (from == null || to == null || from >= to) {
    return null;
  }
  return { from, to };
};

function buildSpeechDecorations(
  doc: ProseMirrorNode,
  from: number,
  to: number,
): DecorationSet {
  const docSize = doc.content.size;
  const safeFrom = Math.max(0, Math.min(from, docSize));
  const safeTo = Math.max(safeFrom, Math.min(to, docSize));
  if (safeFrom >= safeTo) {
    return DecorationSet.empty;
  }
  return DecorationSet.create(doc, [
    Decoration.inline(safeFrom, safeTo, {
      class: DOCUMENT_SPEECH_HIGHLIGHT_CLASS,
    }),
  ]);
}

export const DocumentSpeechHighlight = Extension.create({
  name: 'documentSpeechHighlight',

  addProseMirrorPlugins() {
    return [
      new Plugin<DecorationSet>({
        key: documentSpeechHighlightPluginKey,
        state: {
          init: () => DecorationSet.empty,
          apply(tr, old) {
            const meta = tr.getMeta(documentSpeechHighlightPluginKey) as
              | DocumentSpeechHighlightRange
              | null
              | undefined;
            if (meta === undefined) {
              return tr.docChanged ? old.map(tr.mapping, tr.doc) : old;
            }
            if (meta === null) {
              return DecorationSet.empty;
            }
            return buildSpeechDecorations(tr.doc, meta.from, meta.to);
          },
        },
        props: {
          decorations(state) {
            return documentSpeechHighlightPluginKey.getState(state) ?? DecorationSet.empty;
          },
        },
      }),
    ];
  },
});

export function setDocumentSpeechHighlight(
  editor: Editor,
  range: DocumentSpeechHighlightRange | null,
): void {
  if (editor.isDestroyed) {
    return;
  }
  const current = documentSpeechHighlightPluginKey.getState(editor.state);
  const currentRange =
    current && current.find().length > 0
      ? { from: current.find()[0]!.from, to: current.find()[0]!.to }
      : null;
  if (rangesEqual(currentRange, range)) {
    return;
  }
  editor.view.dispatch(
    editor.state.tr
      .setMeta(documentSpeechHighlightPluginKey, range)
      .setMeta('addToHistory', false),
  );
}

export function resolveSpokenDocRange(
  doc: ProseMirrorNode,
  sourceRange: DocumentSpeechSourceRange | null,
  spokenPlainRange: DocumentSpeechSpokenPlainRange | null,
): DocumentSpeechHighlightRange | null {
  if (!sourceRange || !spokenPlainRange) {
    return null;
  }
  return mapPlainTextOffsetsToDocRange(
    doc,
    sourceRange.from,
    sourceRange.to,
    spokenPlainRange.from,
    spokenPlainRange.to,
  );
}

export function useDocumentSpeechHighlight(editor: Editor | null): void {
  useEffect(() => {
    if (!editor || editor.isDestroyed) {
      return undefined;
    }

    let lastSourceFrom: number | null = null;
    let lastSourceTo: number | null = null;
    let lastSpokenFrom: number | null = null;
    let lastSpokenTo: number | null = null;

    const apply = (
      sourceRange: DocumentSpeechSourceRange | null,
      spokenPlainRange: DocumentSpeechSpokenPlainRange | null,
    ): void => {
      if (editor.isDestroyed) {
        return;
      }
      const sourceFrom = sourceRange?.from ?? null;
      const sourceTo = sourceRange?.to ?? null;
      const spokenFrom = spokenPlainRange?.from ?? null;
      const spokenTo = spokenPlainRange?.to ?? null;
      if (
        sourceFrom === lastSourceFrom
        && sourceTo === lastSourceTo
        && spokenFrom === lastSpokenFrom
        && spokenTo === lastSpokenTo
      ) {
        return;
      }
      lastSourceFrom = sourceFrom;
      lastSourceTo = sourceTo;
      lastSpokenFrom = spokenFrom;
      lastSpokenTo = spokenTo;
      const range = resolveSpokenDocRange(editor.state.doc, sourceRange, spokenPlainRange);
      setDocumentSpeechHighlight(editor, range);
    };

    const unsubscribe = subscribeDocumentSpeech((state) => {
      apply(state.sourceRange, state.spokenPlainRange);
    });

    return () => {
      unsubscribe();
      if (!editor.isDestroyed) {
        setDocumentSpeechHighlight(editor, null);
      }
    };
  }, [editor]);
}
